import * as Sentry from '@sentry/vue'
import { jwtDecode } from 'jwt-decode'
import posthog from 'posthog-js'

interface AccessTokenPayload {
  userId?: string
  type?: string
  name?: string
  email?: string
  impersonation?: boolean
  impersonatedBy?: string
}

export function syncSentryUserFromAccessToken(accessToken: string) {
  if (!accessToken) {
    Sentry.setUser(null)
    resetPostHogIdentifiedUser()
    return
  }

  try {
    const decoded = jwtDecode<AccessTokenPayload>(accessToken)
    if (!decoded.userId) {
      Sentry.setUser(null)
      resetPostHogIdentifiedUser()
      return
    }

    if (decoded.type === 'guest') {
      Sentry.setUser({ id: decoded.userId })
      identifyPostHogUser(decoded.userId, { user_type: 'guest' })
      return
    }

    if (decoded.impersonation) {
      Sentry.setUser({
        id: decoded.userId,
        email: decoded.email,
        username: decoded.name,
      })
      Sentry.setTag('impersonation', 'true')
      if (decoded.impersonatedBy) {
        Sentry.setTag('impersonatedBy', decoded.impersonatedBy)
      }
      return
    }

    Sentry.setTag('impersonation', 'false')
    Sentry.setUser({
      id: decoded.userId,
      email: decoded.email,
      username: decoded.name,
    })
    identifyPostHogUser(decoded.userId, {
      email: decoded.email,
      name: decoded.name,
      user_type: 'registered',
    })
  } catch {
    Sentry.setUser(null)
    resetPostHogIdentifiedUser()
  }
}

export function clearSentryUser() {
  Sentry.setUser(null)
  Sentry.setTag('impersonation', 'false')

  if (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_POSTHOG_HOST) {
    posthog.reset()
  }
}

function identifyPostHogUser(userId: string, properties: Record<string, string | undefined>) {
  if (import.meta.env.VITE_POSTHOG_PROJECT_TOKEN && import.meta.env.VITE_POSTHOG_HOST) {
    const identifiedUserId = posthog.get_property('$user_id')
    if (identifiedUserId && identifiedUserId !== userId) {
      posthog.reset()
    }
    posthog.identify(userId, properties)
  }
}

function resetPostHogIdentifiedUser() {
  if (
    import.meta.env.VITE_POSTHOG_PROJECT_TOKEN &&
    import.meta.env.VITE_POSTHOG_HOST &&
    posthog.get_property('$user_id')
  ) {
    posthog.reset()
  }
}
